import { vsgModeStatus } from '$src/api/constants';
import AuthorizeDialog from '$src/components/AuthorizeDialog';
import { CustomIcon } from '$src/components/CustomIcon';
import GuardMonitoring from '$src/components/GuardMonitoring';
import MenuSelector from '$src/components/MenuSelector';
import MenuSelectorGroup from '$src/components/MenuSelectorGroup';
import MonitoringZonesDialog from '$src/components/MonitoringZonesDialog';
import NavigationMenu from '$src/components/NavigationMenu';
import NavigationTitle from '$src/components/NavigationTitle';
import WarningWithAction from '$src/components/WarningWithAction';
import useFeature from '$src/hooks/useFeature';
import useLocationContext from '$src/hooks/useLocationContext';
import useModalEvents from '$src/hooks/useModalEvents';
import useStores from '$src/hooks/useStores';
import { routes } from '$src/routes';
import { redirectToLocations, redirectToWatchZones } from '$src/utils/redirect';
import { AlertsIcon, DevicesIcon, HomeIcon, LocationIcon, MotionZonesIcon, StarkIcon, TwofaIcon } from '@ring-icons';
import { observer } from 'mobx-react';
import { generatePath } from 'react-router';
import { useLocation } from 'react-router-dom';

const SETTINGS_SIDER_STRINGS = {
  TITLE: 'Virtual Security Guard Settings',
  MANAGEMENT_TITLE: 'Management',
  DASHBOARD: {
    TITLE: 'Dashboard',
    SUBTITLE: 'A Summary of your Events.',
  },
  AUTHORIZED_DEVICES: {
    TITLE: 'Authorized Devices',
    SUBTITLE: 'Manage Authorized Devices on this Location.',
  },
  MONITORED_DEVICES: {
    TITLE: 'Monitored Cameras',
    SUBTITLE: 'Manage Monitored Cameras on this Location.',
  },
  PRIVATE_GUARD: {
    TITLE: 'Private Guard',
    SUBTITLE: 'Send your own private guard instead of the police during emergencies.',
  },
  ALERT_PREFERENCES: {
    TITLE: 'Alert Preferences',
    SUBTITLE: 'Configure how you want to receive Virtual Security Guard related Notifications and important info.',
  },
  TRIAL_MANAGEMENT: {
    TITLE: 'Trial Management',
    SUBTITLE: 'Manage your trial subscription.',
  },
  LOCATION_OVERVIEW: {
    TITLE: 'Location Overview',
    SUBTITLE: 'Manage your locations.',
  },
  MONITORING_ZONES: {
    TITLE: 'Watch Zones',
    SUBTITLE: 'Manage your watch zones.',
  },
};

function SettingsSiderItems() {
  const { sessionStore, modesStore, locationContextStore } = useStores();
  const locationParam = useLocation();
  const locationId = useLocationContext();
  const { isMobile, authorizeVSG } = sessionStore;
  const { isFreeTrialActive, location } = locationContextStore;
  const { vsgMode, vsgErrorState } = modesStore;
  const [isLocationPausedFeatureEnabled] = useFeature('stark-feature-next-location-paused');
  const [isLocationSelectorSmbCustomersEnabled] = useFeature('stark-feature-next-location-selector-smb-customers');
  const [isMonitoringZonesEnabled] = useFeature('stark-feature-next-video-monitoring-zones');
  const [isPrivateGuardEnabled] = useFeature('stark-feature-next-private-guard');
  const [isSoapsudsGuardEnabled] = useFeature('sp_ss_gd_enabled');
  const { isGuardPatrolActive } = modesStore;
  const shouldRenderVSGUnauthorizedCard = vsgErrorState === vsgModeStatus.AUTH_NOT_FOUND;
  const shouldRenderMonitoringZonesModal = vsgErrorState === vsgModeStatus.MISSING_MONITORING_ZONE && isMonitoringZonesEnabled;
  const [toggleAuthorizeModal, showAuthorizeModal] = useModalEvents();
  const [toggleMonitoringZonesModal, showMonitoringZonesModal] = useModalEvents();
  const isGuardMonitoringEnabled = !vsgModeStatus.EVENTS_EXCEEDED.includes(vsgErrorState);

  const handleMonitoringZonesClick = () => {
    const didRedirect = redirectToWatchZones(locationId);
    if (!didRedirect) {
      toggleMonitoringZonesModal();
    }
  };

  const renderWarning = () => {
    if (shouldRenderVSGUnauthorizedCard) {
      return <WarningWithAction {...vsgMode} onClick={toggleAuthorizeModal} />;
    }
    if (shouldRenderMonitoringZonesModal) {
      return <WarningWithAction {...vsgMode} onClick={handleMonitoringZonesClick} />;
    }
    return <WarningWithAction {...vsgMode} />;
  };

  return (
    <>
      <NavigationMenu title={<NavigationTitle icon={StarkIcon} title={SETTINGS_SIDER_STRINGS.TITLE} />}>
        <MenuSelectorGroup>
          {isMobile && vsgMode && renderWarning()}
          {isMobile && isLocationPausedFeatureEnabled && (
            <>
              <GuardMonitoring enabled={isGuardMonitoringEnabled} />
              <p className="layout__sider__menu--title">{SETTINGS_SIDER_STRINGS.MANAGEMENT_TITLE}</p>
            </>
          )}
          <MenuSelector
            data-testid="event-summary-menu-item"
            icon={HomeIcon}
            selected={locationParam.pathname.includes('events-summary')}
            subtitle={SETTINGS_SIDER_STRINGS.DASHBOARD.SUBTITLE}
            to={generatePath(routes.eventSummary, { locationId })}
          >
            {SETTINGS_SIDER_STRINGS.DASHBOARD.TITLE}
          </MenuSelector>
          <MenuSelector
            data-testid="authorized-devices-menu-item"
            icon={DevicesIcon}
            selected={locationParam.pathname.includes('devices')}
            subtitle={
              !isMonitoringZonesEnabled ? SETTINGS_SIDER_STRINGS.AUTHORIZED_DEVICES.SUBTITLE : SETTINGS_SIDER_STRINGS.MONITORED_DEVICES.SUBTITLE
            }
            to={generatePath(routes.devices, { locationId })}
          >
            {!isMonitoringZonesEnabled ? SETTINGS_SIDER_STRINGS.AUTHORIZED_DEVICES.TITLE : SETTINGS_SIDER_STRINGS.MONITORED_DEVICES.TITLE}
          </MenuSelector>
          {/*
            No ownership gate: the item takes no privileged action here. It either hands off
            to the Ring app/web Watch Zones surface, which authorizes the user itself, or
            shows an informational modal. Gating it on ownership hid Watch Zones from
            installers and location managers, who hold the permission.
          */}
          {isMonitoringZonesEnabled && (
            <MenuSelector
              data-testid="motion-zone-settings-menu-item"
              icon={MotionZonesIcon}
              onClick={handleMonitoringZonesClick}
              subtitle={SETTINGS_SIDER_STRINGS.MONITORING_ZONES.SUBTITLE}
            >
              {SETTINGS_SIDER_STRINGS.MONITORING_ZONES.TITLE}
            </MenuSelector>
          )}
          {isPrivateGuardEnabled && !(isSoapsudsGuardEnabled && isGuardPatrolActive) && (
            <MenuSelector
              data-testid="private-guard-menu-item"
              icon={() => <CustomIcon type="regular" name="private_guard.svg" className="layout__sider__menu--icon" />}
              selected={locationParam.pathname.includes('private-guard')}
              subtitle={SETTINGS_SIDER_STRINGS.PRIVATE_GUARD.SUBTITLE}
              to={generatePath(routes.privateGuard, { locationId })}
            >
              {SETTINGS_SIDER_STRINGS.PRIVATE_GUARD.TITLE}
            </MenuSelector>
          )}
          <MenuSelector
            data-testid="alert-preferences-menu-item"
            icon={AlertsIcon}
            selected={locationParam.pathname.includes('alerts')}
            subtitle={SETTINGS_SIDER_STRINGS.ALERT_PREFERENCES.SUBTITLE}
            to={generatePath(routes.alerts, { locationId })}
          >
            {SETTINGS_SIDER_STRINGS.ALERT_PREFERENCES.TITLE}
          </MenuSelector>
          {isFreeTrialActive && location?.isOwner && (
            <MenuSelector
              data-testid="trial-management-menu-item"
              icon={TwofaIcon}
              selected={locationParam.pathname.includes('trial-management')}
              subtitle={SETTINGS_SIDER_STRINGS.TRIAL_MANAGEMENT.SUBTITLE}
              to={generatePath(routes.trialManagement, { locationId })}
            >
              {SETTINGS_SIDER_STRINGS.TRIAL_MANAGEMENT.TITLE}
            </MenuSelector>
          )}
          {isLocationSelectorSmbCustomersEnabled && (
            <MenuSelector
              data-testid="location-overview-menu-item"
              icon={LocationIcon}
              onClick={redirectToLocations}
              subtitle={SETTINGS_SIDER_STRINGS.LOCATION_OVERVIEW.SUBTITLE}
            >
              {SETTINGS_SIDER_STRINGS.LOCATION_OVERVIEW.TITLE}
            </MenuSelector>
          )}
        </MenuSelectorGroup>
      </NavigationMenu>
      <AuthorizeDialog
        showModal={showAuthorizeModal}
        handleAuthorize={() => authorizeVSG(generatePath(routes.settings, { locationId }))}
        setModalVisible={toggleAuthorizeModal}
      />
      {isMonitoringZonesEnabled && (
        <MonitoringZonesDialog
          showModal={showMonitoringZonesModal}
          handleClick={toggleMonitoringZonesModal}
          locationId={locationId}
          handleCancel={toggleMonitoringZonesModal}
          setModalVisible={toggleMonitoringZonesModal}
        />
      )}
    </>
  );
}

export default observer(SettingsSiderItems);
